/**
 * Shared phone + email normalisation and validation helpers.
 *
 * Handles the common real-world cases where:
 *  - Multiple values are given separated by  /  or  \
 *    e.g.  "9949834609/7337069839"  →  "9949834609"
 *  - Numbers have spaces, dashes, parentheses, dots, + prefix
 *    e.g.  "70759 30292"  →  "7075930292"
 *  - Indian numbers carry a country-code prefix  (+91 / 91)
 *    e.g.  "919515793213"  →  "9515793213"
 *  - International numbers (7–15 digits) are accepted as-is
 *  - Multiple emails separated by  /  or  \
 *    e.g.  "a@x.com/b@y.com"  →  "a@x.com"
 */

// ── Phone ──────────────────────────────────────────────────────────────────

/**
 * Takes the raw string from a form field or spreadsheet cell, picks the first
 * value when multiple are separated by / or \, strips all formatting characters
 * (+, spaces, dashes, parentheses), and returns a clean digit string.
 *
 * For Indian numbers specifically (12 digits starting with 91, or 13 digits
 * starting with +91) the country code is stripped to give a 10-digit number.
 */
export const normalizePhone = (raw) => {
  if (!raw) return '';
  const first = String(raw)
    .split(/[/\\]/)
    .map(s => s.trim())
    .filter(Boolean)[0] || '';

  // Strip everything except digits
  let digits = first.replace(/\D/g, '');

  // Drop leading Indian country code: 91 + 10 digits → 10 digits
  if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2);
  // Drop leading US country code: 1 + 10 digits → 10 digits
  if (digits.length === 11 && digits.startsWith('1'))  digits = digits.slice(1);

  return digits;
};

/**
 * Returns true for any number between 7 and 15 digits (ITU-T E.164 range).
 * This covers Indian 10-digit, UK 11-digit, and most international formats.
 */
export const isValidPhone = (phone) => /^\d{7,15}$/.test(phone);

/**
 * Full validate-and-normalise: returns { cleaned, valid, message }.
 *  cleaned  – the best normalised value (may still be invalid)
 *  valid    – boolean
 *  message  – human-readable error or null
 */
export const validatePhone = (raw) => {
  if (!raw || !String(raw).trim()) {
    return { cleaned: '', valid: false, message: 'Phone number is required' };
  }

  const hasMultiple = /[/\\]/.test(String(raw));
  const cleaned = normalizePhone(raw);

  if (!isValidPhone(cleaned)) {
    return {
      cleaned,
      valid: false,
      message: hasMultiple
        ? `Multiple numbers detected — enter only one valid phone number`
        : `Invalid phone number (got "${cleaned || raw}") — must be 7–15 digits`,
    };
  }

  return { cleaned, valid: true, message: null };
};

// ── Email ──────────────────────────────────────────────────────────────────

/** Picks the first email when multiple are separated by / or \ */
export const normalizeEmail = (raw) => {
  if (!raw) return '';
  return String(raw)
    .split(/[/\\]/)
    .map(s => s.trim())
    .filter(Boolean)[0] || '';
};

/** Basic RFC-style email check */
export const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);

/**
 * Full validate-and-normalise for email. Email is optional in most forms,
 * so an empty value returns valid = true (caller decides if it's required).
 */
export const validateEmail = (raw, { required = false } = {}) => {
  const trimmed = String(raw || '').trim();

  if (!trimmed) {
    return required
      ? { cleaned: '', valid: false, message: 'Email is required' }
      : { cleaned: '', valid: true,  message: null };
  }

  const hasMultiple = /[/\\]/.test(trimmed);
  const cleaned = normalizeEmail(trimmed);

  if (!isValidEmail(cleaned)) {
    return {
      cleaned,
      valid: false,
      message: hasMultiple
        ? `Multiple emails detected — enter only one valid email`
        : `Invalid email address`,
    };
  }

  return { cleaned, valid: true, message: null };
};
